import React from 'react';
import { useStore } from '@nanostores/react';
import { X } from 'lucide-react';
import { cn } from '@ideoz/ui';
import { askLabel, hotEditStore } from '../../stores/hotEdit';
import { GLASS, RISE, SHIMMER } from './surface';

/**
 * The element the founder picked, sitting on the chat composer (Prototype Editor UX plan, step 5;
 * Editor Glass Pass plan).
 *
 * Ask Ideoz used to paste "Change the heading "Today at Acme" (src/pages/Alerts.tsx, line 57): "
 * into the composer, so the founder's first job was to edit a sentence about code. Now they get a
 * picture of what they picked - cropped from its top left so the words stay readable - its name
 * with a few of its words, and an empty line to say what they want.
 */
export function AskElementChip({ className }: { className?: string }): React.ReactElement | null {
  const ask = useStore(hotEditStore.pendingAsk);
  if (!ask) return null;

  const label = askLabel(ask);

  return (
    <div
      className={cn(GLASS, RISE, 'flex h-[46px] items-center gap-2.5 rounded-[13px] py-0 pl-1.5 pr-1.5 text-left', className)}
      data-testid="ask-element-chip"
    >
      {ask.screenshot ? (
        <img
          src={ask.screenshot}
          alt=""
          // The whole element, not a crop of its corner: the picture is drawn with the element
          // ringed inside what it sits in, and cropping threw that away. The box is the shape a
          // screen is, so a picture of part of one nearly fills it instead of floating in it.
          className="h-[38px] w-[62px] shrink-0 rounded-[9px] bg-white object-contain p-px"
          style={{ boxShadow: 'inset 0 0 0 1px rgba(127,127,127,0.25)' }}
        />
      ) : (
        <span className={cn(SHIMMER, 'h-[38px] w-[62px] shrink-0 rounded-[9px]')} aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold">{label}</span>
        <span className="block text-[11px]" style={{ color: 'var(--editor-ink-2)' }}>
          Ideoz will look at this
        </span>
      </span>
      <button
        type="button"
        aria-label="Do not ask about this element"
        className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-lg opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        onClick={() => hotEditStore.pendingAsk.set(null)}
      >
        <X size={13} />
      </button>
    </div>
  );
}
